/**
 * Friendly, operator-facing names for connector tool ids.
 *
 * The approval surfaces (inbox row, detail "what will happen") receive only the raw
 * tool id on the wire (e.g. `email.send`, `dispatch.dispatch_newsletter`). The agent
 * builder already shows humans a friendly name for every grantable tool; showing the
 * bare mono id at the highest-stakes moment (the pre-send review) is a needless
 * cognitive tax. `toolLabel` maps an id to the same human name the builder uses.
 *
 * The mapping is deliberately COPIED from the builder's `TOOL_META` rather than
 * imported, to avoid coupling the approval surfaces to the (large) builder page. A
 * later pass will hoist a single shared catalog and dedupe the two. Keep this table in
 * sync with `AgentBuilderPage`'s `TOOL_META` until then.
 */
const TOOL_LABELS: Record<string, string> = {
  // Email (Gmail connector)
  'email.read': 'Read email',
  'email.search': 'Search email',
  'email.read_thread': 'Read email thread',
  'email.create_draft': 'Draft a reply',
  'email.send': 'Send email',
  // Calendar
  'calendar.create': 'Create calendar event',
  'calendar.list': 'List calendar events',
  'calendar.find_availability': 'Check availability',
  // CRM (HubSpot)
  'crm.search': 'Search CRM',
  'crm.read': 'Read CRM record',
  'crm.associations': 'CRM associations',
  'crm.deals_search': 'Search deals',
  'crm.deal_pipelines': 'Deal pipelines',
  'crm.owners': 'CRM owners',
  // Drive
  'drive.search': 'Search Drive',
  'drive.read': 'Read Drive file',
  'drive.get_metadata': 'Get file details',
  'drive.upload': 'Upload to Drive',
  'drive.update': 'Update Drive file',
  'drive.move': 'Move Drive file',
  'drive.trash': 'Trash Drive file',
  // Newsletter (Dispatch)
  'dispatch.list_newsletters': 'List newsletters',
  'dispatch.list_jobs': 'List newsletter jobs',
  'dispatch.get_job': 'Get job details',
  'dispatch.list_candidates': 'List candidate stories',
  'dispatch.get_newsletter': 'Get newsletter',
  'dispatch.start_run': 'Start newsletter run',
  'dispatch.review_candidates': 'Review candidates',
  'dispatch.generate_newsletter': 'Generate newsletter',
  'dispatch.edit_newsletter': 'Edit newsletter',
  'dispatch.approve_newsletter': 'Approve newsletter',
  'dispatch.dispatch_newsletter': 'Send newsletter',
  // PitchReply
  'pitchreply.list_pitch_decks': 'List pitch decks',
  'pitchreply.get_pitch_deck_links': 'Get pitch deck links',
  'pitchreply.get_one_pager': 'Get one-pager',
  'pitchreply.generate_one_pager': 'Generate one-pager',
  'pitchreply.draft_response_email': 'Draft response email',
};

/** Prettify an unknown id: `foo.bar_baz` → "Bar baz". Presentation-only fallback. */
function prettify(id: string): string {
  const local = id.includes('.') ? id.slice(id.indexOf('.') + 1) : id;
  const words = local.replace(/[._]+/g, ' ').trim();
  if (!words) return id;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The human, operator-facing name for a tool id (e.g. `email.send` → "Send email").
 * Unknown ids fall back to a prettified form of the id's local part so the label is
 * never a bare mono string. Never throws; presentation only.
 */
export function toolLabel(id: string | null | undefined): string {
  if (!id) return 'Unknown action';
  return TOOL_LABELS[id] ?? prettify(id);
}

/**
 * A very short name for tight spaces — the connector/verb without its namespace
 * (e.g. `dispatch.dispatch_newsletter` → "Send newsletter"). Currently identical to
 * {@link toolLabel}; kept as a distinct seam so a denser label can diverge later.
 */
export function toolShortName(id: string | null | undefined): string {
  return toolLabel(id);
}
